import type { Coding } from '@medplum/fhirtypes'
import {
  BLOOD_PRESSURE_CODING,
  BODY_HEIGHT_CODING,
  BODY_MASS_INDEX_CODING,
  BODY_TEMPERATURE_CODING,
  BODY_WEIGHT_CODING,
  DIASTOLIC_BLOOD_PRESSURE_CODING,
  HEART_RATE_CODING,
  loincCoding,
  loincCodingOf,
  RESPIRATORY_RATE_CODING,
  SYSTOLIC_BLOOD_PRESSURE_CODING,
} from '#src/models/loinc.ts'

// The LOINC codes clinicians record most: vitals, the labs commonly captured
// by hand (A1c, lipids, the basic and comprehensive metabolic panel members,
// blood counts) and the screening totals. A search ranks these ahead of the
// rest of LOINC, which orders by wording rather than by use, so "weight"
// finds "Body weight" before "Weight of Thymus gland". Displays are LOINC's
// own, checked with `$lookup`, except the vitals `models/loinc.ts` names.
export const COMMON_OBSERVATION_CODES: readonly Coding[] = [
  // Vitals
  loincCodingOf(BLOOD_PRESSURE_CODING),
  loincCodingOf(SYSTOLIC_BLOOD_PRESSURE_CODING),
  loincCodingOf(DIASTOLIC_BLOOD_PRESSURE_CODING),
  loincCoding('8478-0', 'Mean blood pressure'),
  loincCodingOf(HEART_RATE_CODING),
  loincCodingOf(RESPIRATORY_RATE_CODING),
  loincCodingOf(BODY_TEMPERATURE_CODING),
  loincCoding('20564-1', 'Oxygen saturation in Blood'),
  loincCoding(
    '59408-5',
    'Oxygen saturation in Arterial blood by Pulse oximetry'
  ),
  loincCoding('2708-6', 'Oxygen saturation in Arterial blood'),
  loincCodingOf(BODY_WEIGHT_CODING),
  loincCodingOf(BODY_HEIGHT_CODING),
  loincCodingOf(BODY_MASS_INDEX_CODING),
  loincCoding('8277-6', 'Body surface area'),
  loincCoding('8287-5', 'Head Occipital-frontal circumference by Tape measure'),
  // Nutrition
  loincCoding('75292-3', 'Body weight - Reported --usual'),
  loincCoding('50064-5', 'Ideal body weight'),
  loincCoding('69429-9', 'Metabolic rate --resting'),
  loincCoding('38208-5', 'Pain severity - Reported'),
  loincCoding(
    '72514-3',
    'Pain severity - 0-10 verbal numeric rating [Score] - Reported'
  ),
  // Diabetes
  loincCoding('4548-4', 'Hemoglobin A1c/Hemoglobin.total in Blood'),
  loincCoding('2339-0', 'Glucose [Mass/volume] in Blood'),
  loincCoding('2345-7', 'Glucose [Mass/volume] in Serum or Plasma'),
  loincCoding(
    '41653-7',
    'Glucose [Mass/volume] in Capillary blood by Glucometer'
  ),
  loincCoding('1558-6', 'Fasting glucose [Mass/volume] in Serum or Plasma'),
  // Kidney and electrolytes
  loincCoding('2160-0', 'Creatinine [Mass/volume] in Serum or Plasma'),
  loincCoding(
    '62238-1',
    'Glomerular filtration rate [Volume Rate/Area] in Serum, Plasma or Blood by Creatinine-based formula (CKD-EPI)/1.73 sq M'
  ),
  loincCoding(
    '98979-8',
    'Glomerular filtration rate [Volume Rate/Area] in Serum, Plasma or Blood by Creatinine-based formula (CKD-EPI 2021)/1.73 sq M'
  ),
  loincCoding('3094-0', 'Urea nitrogen [Mass/volume] in Serum or Plasma'),
  loincCoding('2951-2', 'Sodium [Moles/volume] in Serum or Plasma'),
  loincCoding('2823-3', 'Potassium [Moles/volume] in Serum or Plasma'),
  loincCoding('2075-0', 'Chloride [Moles/volume] in Serum or Plasma'),
  loincCoding(
    '2028-9',
    'Carbon dioxide, total [Moles/volume] in Serum or Plasma'
  ),
  loincCoding('17861-6', 'Calcium [Mass/volume] in Serum or Plasma'),
  loincCoding('19123-9', 'Magnesium [Mass/volume] in Serum or Plasma'),
  loincCoding('2777-1', 'Phosphate [Mass/volume] in Serum or Plasma'),
  loincCoding('14959-1', 'Microalbumin/Creatinine [Mass Ratio] in Urine'),
  // Liver
  loincCoding('1751-7', 'Albumin [Mass/volume] in Serum or Plasma'),
  loincCoding(
    '1742-6',
    'Alanine aminotransferase [Enzymatic activity/volume] in Serum or Plasma'
  ),
  loincCoding(
    '1920-8',
    'Aspartate aminotransferase [Enzymatic activity/volume] in Serum or Plasma'
  ),
  loincCoding(
    '6768-6',
    'Alkaline phosphatase [Enzymatic activity/volume] in Serum or Plasma'
  ),
  loincCoding('1975-2', 'Bilirubin.total [Mass/volume] in Serum or Plasma'),
  loincCoding('2885-2', 'Protein [Mass/volume] in Serum or Plasma'),
  // Blood counts
  loincCoding('718-7', 'Hemoglobin [Mass/volume] in Blood'),
  loincCoding(
    '4544-3',
    'Hematocrit [Volume Fraction] of Blood by Automated count'
  ),
  loincCoding('6690-2', 'Leukocytes [#/volume] in Blood by Automated count'),
  loincCoding('777-3', 'Platelets [#/volume] in Blood by Automated count'),
  loincCoding('6301-6', 'INR in Platelet poor plasma by Coagulation assay'),
  // Lipids
  loincCoding('2093-3', 'Cholesterol [Mass/volume] in Serum or Plasma'),
  loincCoding('2085-9', 'Cholesterol in HDL [Mass/volume] in Serum or Plasma'),
  loincCoding(
    '13457-7',
    'Cholesterol in LDL [Mass/volume] in Serum or Plasma by calculation'
  ),
  loincCoding('2571-8', 'Triglyceride [Mass/volume] in Serum or Plasma'),
  // Thyroid and vitamins
  loincCoding('3016-3', 'Thyrotropin [Units/volume] in Serum or Plasma'),
  loincCoding(
    '2132-9',
    'Cobalamin (Vitamin B12) [Mass/volume] in Serum or Plasma'
  ),
  loincCoding('1988-5', 'C reactive protein [Mass/volume] in Serum or Plasma'),
  loincCoding('2276-4', 'Ferritin [Mass/volume] in Serum or Plasma'),
  loincCoding(
    '1989-3',
    '25-hydroxyvitamin D3 [Mass/volume] in Serum or Plasma'
  ),
  // Screening totals
  loincCoding(
    '44261-6',
    'Patient Health Questionnaire 9 item (PHQ-9) total score [Reported]'
  ),
  loincCoding(
    '55758-7',
    'Patient Health Questionnaire 2 item (PHQ-2) total score [Reported]'
  ),
  loincCoding(
    '70274-6',
    'Generalized anxiety disorder 7 item (GAD-7) total score [Reported.PHQ]'
  ),
]

const COMMON_KEYS = new Set(
  COMMON_OBSERVATION_CODES.map(({ system, code }) => `${system}|${code}`)
)

export function isCommonObservationCode(
  coding: Pick<Coding, 'system' | 'code'>
): boolean {
  return COMMON_KEYS.has(`${coding.system}|${coding.code}`)
}
